import React from 'react';
import { useTranslation } from 'react-i18next';
import { AutoSizer, Table, Column, InfiniteLoader } from 'react-virtualized';
import { Empty, Spin } from 'antd';
import { AccountConfigurationFilter } from '../../../../../types/account_configurations';
import { AccountFilter } from '../../../../../types/filter';
import { Brand } from '../../../../../types/brand';
import { UserName, UserType } from '../../../../../types/user';
import { getPageLimit, getTimezoneOffset } from '../../../../utils/Utils';
import { PDM_INTERNAL } from '../../../../constants/UserNameConstants';
import { isPDMUser } from '../../../../utils/UserUtils';
import ConfigurationHeaderCell from './ConfigurationHeaderCell';
import ConfigurationUsedChannels from './ConfigurationUsedChannels';
import ConfigurationUsedCustomFields from './ConfigurationUsedCustomFields';
import ConfigurationLastUsedApi from './ConfigurationLastUsedApi';

type AccountFiltersTableProps = {
  fetchingAccountFilters: boolean;
  filters: AccountConfigurationFilter[];
  brands: Brand[];
  users: UserName[];
  hasMoreData: boolean;
  fetchNextAccountFilters: (page: number) => Promise<any>;
  selectedNames: string[];
  selectedUserIds: number[];
  selectedGlobalStatusIds: number[];
  selectedBrandIds: number[];
  allFilterNames: AccountFilter[];
  handleFilterByColumns: (
    names: string[],
    userIds: number[],
    globalStatuses: number[],
    brandIds: number[]
  ) => void;
  sortColumn?: string;
  sortOrder?: string;
  onSort: (column: string, order: string) => void;
  lastFetchedPageRef: React.MutableRefObject<number>;
};

const ROW_HEIGHT = 50;

const AccountFiltersTable: React.FC<AccountFiltersTableProps> = ({
  fetchingAccountFilters,
  filters,
  brands,
  users,
  hasMoreData,
  fetchNextAccountFilters,
  sortColumn,
  sortOrder,
  onSort,
  selectedNames,
  selectedUserIds,
  selectedGlobalStatusIds,
  selectedBrandIds,
  allFilterNames,
  handleFilterByColumns,
  lastFetchedPageRef,
}) => {
  const { t } = useTranslation();
  const timezoneOffset = React.useMemo(() => getTimezoneOffset(), []);
  const pageSize = React.useMemo(() => getPageLimit(), []);

  const nameListValues = React.useMemo(() => {
    const uniqueNames = new Map<string, { id: number; name: string }>();
    allFilterNames.forEach(f => {
      if (f.name && !uniqueNames.has(f.name)) {
        uniqueNames.set(f.name, { id: f.id, name: f.name });
      }
    });
    return Array.from(uniqueNames.values());
  }, [allFilterNames]);

  const nameToIdMap = React.useMemo(() => {
    const map = new Map<string, number>();
    nameListValues.forEach(item => {
      map.set(item.name, item.id);
    });
    return map;
  }, [nameListValues]);

  const idToNameMap = React.useMemo(() => {
    const map = new Map<number, string>();
    nameListValues.forEach(item => {
      map.set(item.id, item.name);
    });
    return map;
  }, [nameListValues]);

  const selectedNameIds = React.useMemo(() => {
    return selectedNames.map(name => nameToIdMap.get(name) || 0).filter(id => id !== 0);
  }, [selectedNames, nameToIdMap]);

  const userListValues = React.useMemo(() => {
    const uniqueUsers = new Map<string, { id: number; name: string }>();

    users.forEach(user => {
      const pdmUser = isPDMUser({ email: user.email } as UserType);
      const nameValue = pdmUser ? PDM_INTERNAL : `${user.first_name} ${user.last_name}`;

      if (!uniqueUsers.has(nameValue)) {
        uniqueUsers.set(nameValue, {
          id: user.id,
          name: nameValue,
        });
      }
    });

    return Array.from(uniqueUsers.values());
  }, [users]);

  const userNameToIdsMap = React.useMemo(() => {
    const usersMap = new Map<string, number[]>();

    users.forEach(user => {
      const pdmUser = isPDMUser({ email: user.email } as UserType);
      const nameValue = pdmUser ? PDM_INTERNAL : `${user.first_name} ${user.last_name}`;

      if (!usersMap.has(nameValue)) {
        usersMap.set(nameValue, []);
      }
      usersMap.get(nameValue)!.push(user.id);
    });

    return usersMap;
  }, [users]);

  const globalStatusValues = React.useMemo(
    () => [
      { id: 0, name: t('common:no') },
      { id: 1, name: t('common:yes') },
    ],
    [t]
  );

  const brandListValues = React.useMemo(
    () => brands.map(b => ({ id: b.id, name: b.name })),
    [brands]
  );

  const renderCreatedBy = (createdBy: string) => {
    const pdmUser = isPDMUser({ email: createdBy } as UserType);
    return pdmUser ? PDM_INTERNAL : createdBy;
  };

  const renderFilterGlobalStatus = (filterPublic: number) => {
    if (!filterPublic) {
      return <span>{t('common:no')}</span>;
    }

    return <span>{t('common:yes')}</span>;
  };

  const renderFilterBrand = (brandId: number) => {
    if (!brandId) {
      return <span />;
    }

    const brand = brands.find(b => b.id === brandId);
    return brand ? <span>{brand.name}</span> : <span />;
  };

  const createNameHeaderCell = () => (
    <ConfigurationHeaderCell
      label={t('configurations:name')}
      filterValues={nameListValues}
      handleFilterChange={nameIds => {
        const names = nameIds.map(id => idToNameMap.get(id) || '').filter(name => name !== '');
        handleFilterByColumns(names, selectedUserIds, selectedGlobalStatusIds, selectedBrandIds);
      }}
      selectedFilterIds={selectedNameIds}
      showSearch
      sortColumn="name"
      currentSortColumn={sortColumn}
      sortOrder={sortOrder}
      onSort={order => onSort('name', order)}
    />
  );

  const createCreatedByHeaderCell = () => (
    <ConfigurationHeaderCell
      label={t('configurations:createdBy')}
      filterValues={userListValues}
      handleFilterChange={selectedUserIds => {
        let allSelectedUserIds: number[] = [];
        selectedUserIds.forEach(selectedUserId => {
          const userName = userListValues.find(u => u.id === selectedUserId)?.name;
          if (userName) {
            const ids = userNameToIdsMap.get(userName) || [];
            allSelectedUserIds.push(...ids);
          }
        });
        allSelectedUserIds = [...new Set(allSelectedUserIds)];
        handleFilterByColumns(
          selectedNames,
          allSelectedUserIds,
          selectedGlobalStatusIds,
          selectedBrandIds
        );
      }}
      selectedFilterIds={selectedUserIds}
      showSearch
      sortColumn="created_by"
      currentSortColumn={sortColumn}
      sortOrder={sortOrder}
      onSort={order => onSort('created_by', order)}
    />
  );

  const createGlobalStatusHeaderCell = () => (
    <ConfigurationHeaderCell
      label={t('configurations:filterGlobalStatus')}
      filterValues={globalStatusValues}
      handleFilterChange={globalStatuses => {
        handleFilterByColumns(selectedNames, selectedUserIds, globalStatuses, selectedBrandIds);
      }}
      selectedFilterIds={selectedGlobalStatusIds}
      filterHeight={100}
      sortColumn="public"
      currentSortColumn={sortColumn}
      sortOrder={sortOrder}
      onSort={order => onSort('public', order)}
    />
  );

  const createBrandHeaderCell = () => (
    <ConfigurationHeaderCell
      label={t('configurations:brand')}
      filterValues={brandListValues}
      handleFilterChange={brandIds => {
        handleFilterByColumns(selectedNames, selectedUserIds, selectedGlobalStatusIds, brandIds);
      }}
      selectedFilterIds={selectedBrandIds}
      showSearch
      sortColumn="parent_owner_brand_id"
      currentSortColumn={sortColumn}
      sortOrder={sortOrder}
      onSort={order => onSort('parent_owner_brand_id', order)}
    />
  );

  const handleTableScroll = ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => {
    const nextPage = Math.ceil(stopIndex / pageSize);
    const currentPage = Math.ceil(startIndex / pageSize);
    const lastPage = currentPage > startIndex / pageSize;

    if (lastPage) {
      return Promise.resolve();
    }

    if (fetchingAccountFilters || !hasMoreData) {
      return Promise.resolve();
    }

    lastFetchedPageRef.current = nextPage;
    return fetchNextAccountFilters(nextPage);
  };

  return (
    <AutoSizer>
      {({ height, width }) => (
        <div style={{ height: height - 1, width }}>
          <InfiniteLoader
            isRowLoaded={({ index }) => !!filters[index]}
            loadMoreRows={handleTableScroll}
            rowCount={hasMoreData ? filters.length + pageSize : filters.length}
            minimumBatchSize={pageSize}
            threshold={10}
          >
            {({ onRowsRendered, registerChild }) => (
              <Table
                height={height - 1}
                width={width - 2}
                onRowsRendered={onRowsRendered}
                ref={registerChild}
                headerHeight={ROW_HEIGHT}
                rowHeight={ROW_HEIGHT}
                rowCount={filters.length}
                rowGetter={({ index }) => filters[index]}
                noRowsRenderer={() =>
                  fetchingAccountFilters ? (
                    <Spin className="spinner-center" style={{ marginTop: '20px' }} />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t('configurations:noFilters')}
                    />
                  )
                }
              >
                <Column label={t('configurations:id')} dataKey="id" width={100} />
                <Column
                  headerRenderer={createNameHeaderCell}
                  dataKey="name"
                  width={120}
                  flexGrow={1}
                />
                <Column
                  headerRenderer={createCreatedByHeaderCell}
                  dataKey="created_by"
                  width={250}
                  cellRenderer={({ cellData }) => renderCreatedBy(cellData)}
                  flexGrow={1}
                />
                <Column
                  headerRenderer={createGlobalStatusHeaderCell}
                  dataKey="public"
                  width={110}
                  cellRenderer={({ cellData }) => renderFilterGlobalStatus(cellData)}
                />
                <Column
                  headerRenderer={createBrandHeaderCell}
                  dataKey="parent_owner_brand_id"
                  width={200}
                  cellRenderer={({ cellData }) => renderFilterBrand(cellData)}
                  flexGrow={1}
                />
                <Column
                  label={t('configurations:usedChannels')}
                  dataKey="used_channels"
                  width={250}
                  cellRenderer={({ cellData }) => <ConfigurationUsedChannels channels={cellData} />}
                />
                <Column
                  label={t('configurations:usedCustomFields')}
                  dataKey="used_custom_fields"
                  width={250}
                  cellRenderer={({ cellData }) => (
                    <ConfigurationUsedCustomFields customFields={cellData} />
                  )}
                />
                <Column
                  label={`${t('configurations:lastUsedApi')} (${timezoneOffset})`}
                  dataKey="last_used_api"
                  width={250}
                  cellRenderer={({ cellData }) => (
                    <ConfigurationLastUsedApi lastUsedApi={cellData} />
                  )}
                />
              </Table>
            )}
          </InfiniteLoader>
        </div>
      )}
    </AutoSizer>
  );
};

export default AccountFiltersTable;
